/*
 * Boot. Clerk-gated from day one (vanilla @clerk/clerk-js, no React), exactly like
 * the gateway app — auth is REAL Clerk in every environment, no dev bypass. The dev
 * server runs on local.vitesseautomation.ai:3001 over HTTPS so Clerk's production
 * keys load (see vite.config).
 *
 * This app is a thin main-thread Connect client: it owns a TokenHolder, builds the
 * FleetControl transport once, and hands it to <fleet-admin-app>. No backend worker —
 * there's no high-frequency data plane to isolate, just unary control RPCs.
 */
import { Clerk } from '@clerk/clerk-js';
import { dark } from '@clerk/themes';
// Design tokens (CSS custom properties) — loaded FIRST so every var(--*) resolves.
import '@vitesse/base-ui/tokens.css';
// Global SHARED stylesheet for the light-DOM content (the gateway-card grid) — same
// base card + grid + badge SCSS as the gateway app.
import '../styles/app.scss';
// Registers base-ui's custom elements (vitesse-logo, vitesse-theme-toggle, …) and
// exposes the theme + i18n APIs.
import '@vitesse/base-ui';
import { initTheme, getResolvedTheme, onThemeChange } from '@vitesse/base-ui/theme';
import { initI18n, onLocaleChange } from '@vitesse/base-ui/i18n';
import './app';
import type { FleetAdminApp } from './app';
import { makeFleetTransport, type TokenHolder } from '../core/fleet-client';
import { registerFleetI18n } from '../core/i18n';
import { CLERK_PUBLISHABLE_KEY } from '../core/env';
import { AiClient } from '@vitesse/base-ui/ai';
// Reuse the SHARED identity/authz (same Clerk-session resolution + admin gate the
// gateway app uses, mirroring the backend authz catalog) — no reinvention.
import {
  vitesseClerkAppearance,
  identityFrom,
  isOrgAdmin,
  isSuperAdmin,
  orgNamesFrom,
  applyVitesseBranding,
  fatal,
  mountSignIn,
  mountNotAuthorized,
  bindIdentity,
  mountOrgSwitcher,
  getSession,
  type ClerkSwitcherHost,
  fetchAuditEvents,
} from '@vitesse/base-ui';
import type { IdentityHost } from '@vitesse/base-ui';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { listOrgMembers, listOrgRoles, setOrgRoles, setUserGrants } from '@vitesse/base-ui/rbac-rpc';
import '../core/session';

const root = document.getElementById('root')!;

/*
 * The ONLY thing the mounted app needs from the identity provider. Everything below
 * this line is provider-agnostic — the seam that keeps the header/RBAC/AI wiring out
 * of any one SDK's shape.
 */
interface AppChrome {
  /** Feeds identityFrom/orgNamesFrom/mountOrgSwitcher — the same object bindIdentity got. */
  identity: IdentityHost & ClerkSwitcherHost;
  /** Read PER CALL, never captured: the AI plane outlives any single token (~60s). */
  getToken: () => Promise<string | null>;
  userName: string;
  /** Mounts Clerk's UserButton into `el`. */
  mountUser: (el: HTMLDivElement) => void;
  /** Clerk's own appearance object — the app's theme, unchanged. */
  appearance: unknown;
}

async function main(): Promise<void> {
  applyVitesseBranding('Fleet'); // tab → "Vitesse - Fleet" + the V favicon (shared, base-ui)
  initTheme(); // apply stored/auto theme before anything renders
  await initI18n(); // load stored/detected language before first render
  registerFleetI18n(); // add the fleet-admin's own `fleet` namespace onto the singleton

  await boot();
}

function clerkAppearance() {
  return vitesseClerkAppearance(dark, getResolvedTheme() === 'dark');
}

async function boot(): Promise<void> {
  const clerk = new Clerk(CLERK_PUBLISHABLE_KEY);
  const getToken = (): Promise<string | null> => clerk.session?.getToken() ?? Promise.resolve(null);
  // Cache-skipping mint, for stale-token recovery: a refresh that re-reads the cache
  // gets the same expired token back and whatever it was recovering re-opens in a loop.
  const getFreshToken = (): Promise<string | null> =>
    clerk.session?.getToken({ skipCache: true }) ?? Promise.resolve(null);

  // The token holder the FleetControl transport reads. `refresh` is the recovery path
  // the authRetryInterceptor calls on a 401 — force-refreshed, per above, so a stale
  // JWT self-heals instead of re-reading the same expired one from the cache.
  const holder: TokenHolder = { token: null, refresh: getFreshToken };
  const transport = makeFleetTransport(holder);
  const aiReady = spawnAi(getToken, getFreshToken);

  try {
    await clerk.load();
  } catch (err) {
    fatal(root, 'Auth failed to load', err);
    return;
  }

  // Identity now, and again on every Clerk tick. This is what makes a gated control
  // re-render when the user switches org — without it the UI keeps rendering the
  // permissions of the org they just left.
  bindIdentity(clerk as unknown as IdentityHost);

  let appMounted = false;
  const sync = async (): Promise<void> => {
    if (clerk.user && !appMounted) {
      // Admin-only surface: super-admin (Vitesse-org admin) OR admin of the active
      // org. A signed-in NON-admin sees the access wall, not the fleet. The server
      // (fleet-control authz) is the real gate; this mirrors it for UX.
      if (!isOrgAdmin(identityFrom(clerk as unknown as IdentityHost))) {
        mountNotAuthorized(root, clerk as unknown as Parameters<typeof mountNotAuthorized>[1], 'The fleet admin');
        return;
      }
      appMounted = true;
      holder.token = await getToken();
      mountApp(
        {
          identity: clerk as unknown as IdentityHost & ClerkSwitcherHost,
          getToken,
          userName: clerk.user?.firstName ?? clerk.user?.username ?? '',
          // Clerk's own UserButton — profile, account management and sign-out,
          // prebuilt and themed with this app's appearance.
          mountUser: (el) => {
            try {
              clerk.unmountUserButton(el);
            } catch {
              /* not mounted yet */
            }
            try {
              clerk.mountUserButton(el, { appearance: clerkAppearance(), userProfileProps: { appearance: clerkAppearance() } });
            } catch {
              /* cosmetic */
            }
          },
          appearance: clerkAppearance(),
        },
        transport,
        await aiReady,
      );
    } else if (!clerk.user && appMounted) {
      location.reload(); // signed out → clean teardown
    } else if (!clerk.user) {
      mountSignIn(root, clerk as unknown as Parameters<typeof mountSignIn>[1], { darkTheme: dark });
    }
  };

  clerk.addListener(() => void sync());
  await sync();

  // Keep the pushed token fresh proactively (Clerk tokens are ~60s); the 401-retry
  // is the backstop, this avoids most retries. NOTE: intervals are throttled in
  // hidden tabs and paused during sleep — that's why the 401-retry exists; this
  // timer is the cheap warm path.
  const pushToken = (): void => {
    void getToken().then(
      (t) => (holder.token = t),
      () => {
        /* keep the last token; the 401-retry (holder.refresh) recovers on the next call */
      },
    );
  };
  setInterval(pushToken, 30_000);
  // Waking from a hidden tab / sleep: push a fresh token immediately instead of
  // waiting out the (possibly long-overdue) interval tick.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') pushToken();
  });
}

/*
 * The AI plane's own worker (a second OS thread), spawned in the boot window so it is warm
 * by the time anyone opens the rail, and idling cheaply until they do. The entry is per-app
 * because a bundler resolves a worker URL only from a literal in this app's own source;
 * everything behind it is the shared base-ui AI plane, with `platform: 'fleet'` selecting this
 * surface's tools and persona server-side.
 */
function spawnAi(
  getToken: () => Promise<string | null>,
  getFreshToken: () => Promise<string | null>,
): Promise<AiClient> {
  return AiClient.create(
    new Worker(new URL('../workers/ai.worker.ts', import.meta.url), { type: 'module', name: 'vitesse-ai' }),
    getToken,
    getFreshToken,
  );
}

function mountApp(chrome: AppChrome, transport: ReturnType<typeof makeFleetTransport>, ai: AiClient): void {
  // Strip any leftover OAuth hash for a clean URL (in place, no reload).
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);

  root.replaceChildren();
  const app = document.createElement('fleet-admin-app') as FleetAdminApp;
  app.transport = transport;
  // The Clerk singleton — the app opens the org Billing page from it when a gated
  // surface's Upgrade button is pressed (it satisfies ClerkSwitcherHost structurally).
  app.identity = chrome.identity;
  app.ai = ai; // the AI plane handle; the manager + handshake are the app's concern
  // Read per call, not captured once: the AI plane outlives any single token (~60s).
  app.aiToken = chrome.getToken;
  app.userName = chrome.userName;
  app.orgNames = orgNamesFrom(chrome.identity);
  // Vitesse super-admin (active org = Vitesse + admin). Server authz is the real
  // boundary (super-admin → all orgs; else active org only); this flag is for UX +
  // gating the dev-only ?demo aid so even synthetic data respects the org model.
  app.superAdmin = isSuperAdmin(identityFrom(chrome.identity));

  // Clerk UserButton + OrgSwitcher live in the LIGHT DOM (Clerk injects CSS into
  // document.head). We hand them to the app as slotted children so the header
  // projects them into its right-aligned actions. Re-mount on theme change to swap
  // baseTheme. hidePersonal forces an active-org context (super-admin = admin role
  // in the Vitesse org, so the active org must be that org).
  const userDiv = document.createElement('div');
  userDiv.slot = 'user';
  const orgDiv = document.createElement('div');
  orgDiv.slot = 'org';
  // Hand the Clerk light-DOM nodes to the app as PROPERTIES — it's light-DOM now, so it
  // renders them into its template (carrying slot="user"/"org") and the shell projects
  // them into the header. appendChild would be wiped by the light-DOM render.
  app.userEl = userDiv;
  app.orgEl = orgDiv;
  root.appendChild(app);

  /*
   * The org switcher, permissions page included — see base-ui/mountOrgSwitcher.
   * This app supplies only what is genuinely its own: the write, which goes
   * through the fleet transport it owns, and whether this viewer may author,
   * which it asks the same annotation the server enforces.
   *
   * Behind the switcher sits Clerk's OrganizationProfile, which is where the RBAC
   * and billing pages are mounted as custom pages.
   */
  const mountOrg = () =>
    mountOrgSwitcher(chrome.identity, orgDiv, {
      appearance: chrome.appearance,
      canEdit: () => getSession()?.allowsRpc(FleetControl.method.setUserGrants) === true,
      // Its OWN annotation, not the neighbouring one: authoring roles is a
      // different RPC selling a different tier, so borrowing the answer would
      // offer a Save the server refuses.
      canEditRoles: () => getSession()?.allowsRpc(FleetControl.method.setOrgRoles) === true,
      save: (userId, payload) => setUserGrants(transport, userId, payload),
      // THE ROSTER, from the server rather than the SDK's membership list: it is the
      // same list the grants are authored against, so the editor cannot show a person
      // the write path does not know about.
      members: () => listOrgMembers(transport),
      roles: () => listOrgRoles(transport),
      saveRoles: (roles) => setOrgRoles(transport, roles),
      billing: { getToken: chrome.getToken },
      // The org's history. Gated server-side on org:audit:read; the tab renders for
      // anyone who may read it and the worker refuses anyone who may not.
      audit: { events: () => fetchAuditEvents(chrome.getToken) },
    });
  const mountUser = () => chrome.mountUser(userDiv);
  mountUser();
  mountOrg();
  // The re-mount is what re-renders a mounted widget in the new theme/locale.
  onThemeChange(() => {
    mountUser();
    mountOrg();
  });
  onLocaleChange(() => {
    mountUser();
    mountOrg();
  });
}

void main();
